<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="850"
  >
    <!--begin::Modal dialog-->
    <div class="bg-white poppins">
      <!--begin::Modal header-->
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ $t("add_new_forecast") }}</h4>
        <!--begin::Close-->
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
        <!--end::Close-->
      </div>
      <!--end::Modal header-->
      <!--begin::Modal body-->
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <div class="row flex-grow-0 flex-shrink-0" v-if="serverData">
          <div v-if="serverData.customers" class="col-12 h-75px">
            <v-autocomplete
              v-model="formData.customer_id"
              :label="$t('customer')"
              item-text="text"
              item-value="index"
              :items="serverData.customers"
              clearable
              outlined
              dense
              :error-messages="customerErrors"
              @input="$v.formData.customer_id.$touch()"
              @blur="$v.formData.customer_id.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-autocomplete
              v-model="formData.type"
              :label="$t('type')"
              item-text="text"
              item-value="index"
              :items="serverData.types"
              clearable
              outlined
              dense
              :error-messages="typeErrors"
              @input="$v.formData.type.$touch()"
              @blur="$v.formData.type.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-text-field
              v-model="formData.year"
              :label="$t('year')"
              clearable
              outlined
              dense
              :error-messages="yearErrors"
              @input="$v.formData.year.$touch()"
              @blur="$v.formData.year.$touch()"
            />
          </div>
          <div v-if="formData.type === 1" class="col-6 h-75px">
            <v-autocomplete
              v-model="formData.entity"
              :label="$t('quarter')"
              item-text="text"
              item-value="index"
              :items="serverData.quarters"
              clearable
              outlined
              dense
              :error-messages="entityErrors"
              @input="$v.formData.entity.$touch()"
              @blur="$v.formData.entity.$touch()"
            />
          </div>
          <div v-else-if="formData.type === 2" class="col-6 h-75px">
            <v-autocomplete
              v-model="formData.entity"
              :label="$t('month')"
              item-text="text"
              item-value="index"
              :items="serverData.months"
              clearable
              outlined
              dense
              :error-messages="entityErrors"
              @input="$v.formData.entity.$touch()"
              @blur="$v.formData.entity.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-text-field
              v-model="formData.inbound_pieces"
              :label="$t('inbound_pieces')"
              clearable
              outlined
              dense
              :error-messages="inboundPiecesErrors"
              @input="$v.formData.inbound_pieces.$touch()"
              @blur="$v.formData.inbound_pieces.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-text-field
              v-model="formData.inbound_pallets"
              :label="$t('inbound_pallets')"
              clearable
              outlined
              dense
              :error-messages="inboundPalletsErrors"
              @input="$v.formData.inbound_pallets.$touch()"
              @blur="$v.formData.inbound_pallets.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-text-field
              v-model="formData.uae_orders"
              :label="$t('uae_orders')"
              clearable
              outlined
              dense
              :error-messages="uaeOrdersErrors"
              @input="$v.formData.uae_orders.$touch()"
              @blur="$v.formData.uae_orders.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-text-field
              v-model="formData.gcc_orders"
              :label="$t('gcc_orders')"
              clearable
              outlined
              dense
              :error-messages="gccOrdersErrors"
              @input="$v.formData.gcc_orders.$touch()"
              @blur="$v.formData.gcc_orders.$touch()"
            />
          </div>
          <div class="col-6 h-75px">
            <v-text-field
              v-model="formData.global_orders"
              :label="$t('global_orders')"
              clearable
              outlined
              dense
              :error-messages="globalOrdersErrors"
              @input="$v.formData.global_orders.$touch()"
              @blur="$v.formData.global_orders.$touch()"
            />
          </div>
        </div>

        <div
          class="my-4 min-h-75px d-flex flex-grow-1 align-end justify-center"
        >
          <button
            type="reset"
            class="btn btn-light me-3 px-5 py-3 ls1"
            @click="resetFormData"
          >
            {{ $t("clear") }}
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitForm"
          >
            {{ $t("create") }}
          </button>
        </div>
        <!--end::Actions-->
      </div>
      <!--end::Modal body-->
    </div>
    <!--end::Modal dialog-->
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minValue } from "vuelidate/lib/validators";
import swalAdded from "@/core/services/AlertServices/SwalAdded";

import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";

export default {
  name: "ActionsDialog",
  props: ["refresher", "pageLoader"],
  mixins: [validationMixin, fieldValueValidation],
  validations: {
    formData: {
      customer_id: {
        required: requiredIf(function () {
          return this.serverData.customers;
        }),
      },
      type: { required },
      year: {
        required,
        minValue: minValue(new Date().getFullYear()), // Ensure year is at least 2025
      },
      entity: { required },
      inbound_pieces: { required },
      inbound_pallets: { required },
      uae_orders: { required },
      gcc_orders: { required },
      global_orders: { required },
      // month: {
      //   required: requiredIf(function () {
      //     return this.formData.type === 2;
      //   }),
      // },
      // quarter: {
      //   required: requiredIf(function () {
      //     return this.formData.type === 1;
      //   }),
      // },
    },
  },
  data: () => ({
    dialog: false,
    formData: {
      customer_id: null,
      year: null,
      type: 1,
      entity: null,
      inbound_pieces: null,
      inbound_pallets: null,
      uae_orders: null,
      gcc_orders: null,
      global_orders: null,
    },
    serverData: null,
  }),
  methods: {
    loadDataFromServer() {
      if (!this.serverData) {
        this.pageLoader(true);
        ApiService.post("/settings/forecasts/create")
          .then(({ data }) => {
            this.serverData = data;
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      }
      this.dialog = true;
    },
    toggleModal() {
      if (this.dialog) {
        this.resetFormData();
        // this.dialog = !this.dialog;
        this.dialog = false;
      } else {
        this.loadDataFromServer();
      }
    },
    handleFormValidation(fieldName) {
      const errors = [];
      if (!this.$v.formData[fieldName].$dirty) return errors;
      if ("required" in this.$v.formData[fieldName]) {
        !this.$v.formData[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      if ("minValue" in this.$v.formData[fieldName]) {
        !this.$v.formData[fieldName].minValue &&
          errors.push(
            this.$t("year_must_be_current_or_later", {
              year: new Date().getFullYear(),
            })
          );
      }
      return errors;
    },
    submitForm() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.pageLoader(true);

        ApiService.post("settings/forecasts/store", this.formData)
          .then(() => {
            swalAdded();
            this.toggleModal();
            this.refresher();
          })
          .catch((e) => {
            // console.error(e);
          })
          .finally(() => {
            this.pageLoader(false);
          });
      }
    },
    resetFormData() {
      this.formData = {
        customer_id: null,
        type: 1,
        year: null,
        entity: null,
        inbound_pieces: null,
        inbound_pallets: null,
        uae_orders: null,
        gcc_orders: null,
        global_orders: null,
      };
    },
  },
  computed: {
    customerErrors: function () {
      return this.handleFormValidation("customer_id");
    },
    typeErrors: function () {
      return this.handleFormValidation("type");
    },
    yearErrors: function () {
      return this.handleFormValidation("year");
    },
    quartersErrors: function () {
      return this.handleFormValidation("quarter");
    },
    monthsErrors: function () {
      return this.handleFormValidation("month");
    },
    entityErrors: function () {
      return this.handleFormValidation("entity");
    },
    inboundPiecesErrors: function () {
      return this.handleFormValidation("inbound_pieces");
    },
    inboundPalletsErrors: function () {
      return this.handleFormValidation("inbound_pallets");
    },
    uaeOrdersErrors: function () {
      return this.handleFormValidation("uae_orders");
    },
    gccOrdersErrors: function () {
      return this.handleFormValidation("gcc_orders");
    },
    globalOrdersErrors: function () {
      return this.handleFormValidation("global_orders");
    },
  },
};
</script>
